import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Tooltip,
  TextField,
  MenuItem,
  styled,
} from "@mui/material";
import Header from "../../component/users/Header";
import Footer from "../../component/users/Footer";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GuestUserRistModal from "../../component/Modals/user/guestUserModalRist/Modal";
import profilePlaceholder from "../../assets/customerAssets/profile-face-placeholder.png";
import cameraIcon from "../../assets/customerAssets/icons/camera.svg";
import { countryCodes } from "../../helper/commonFunctions";
import { ADVOCACY_PLATFORM_LABEL } from "../../utils/homeFlow";
import {
  accountEmail,
  accountFullName,
  contestantAgeValue,
  dobFromAge,
} from "../../utils/contestantProfile";
import {
  competitionMediaAccept,
  isCompetitionImageFile,
  isCompetitionVideoFile,
  resolveCompetitionMediaType,
} from "../../utils/competitionEventMedia";
import { isCompetitionRegistrationOpen } from "../../utils/competitionRegistration";

const DEFAULT_COUNTRY_CODE = "+44 United Kingdom";

function splitStoredMobile(stored) {
  const raw = String(stored || "").trim();
  if (!raw) return { countryCode: DEFAULT_COUNTRY_CODE, mobile: "" };
  const sorted = [...countryCodes].sort(
    (a, b) => b.split(" ")[0].length - a.split(" ")[0].length
  );
  for (const code of sorted) {
    const prefix = code.split(" ")[0];
    if (raw.startsWith(prefix)) {
      return {
        countryCode: code,
        mobile: raw.slice(prefix.length).replace(/\D/g, ""),
      };
    }
  }
  return {
    countryCode: DEFAULT_COUNTRY_CODE,
    mobile: raw.replace(/\D/g, ""),
  };
}

const MAX_VIDEO_SEC = 600;

const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderRadius: "10px",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#FFCD1F" },
    "&.Mui-disabled": {
      backgroundColor: "rgba(255,255,255,0.03)",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "rgba(255,255,255,0.55)",
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.55)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#FFCD1F" },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(255,255,255,0.4)",
  },
};

const Pill = styled(Button, {
  shouldForwardProp: (prop) =>
    !["selected", "pillDisabled"].includes(prop),
})(({ selected, pillDisabled }) => ({
  margin: "6px",
  padding: "15px 20px",
  minHeight: 52,
  borderRadius: 40,
  textTransform: "none",
  fontFamily: "Arial, sans-serif",
  fontWeight: 500,
  fontSize: 17,
  lineHeight: "17px",
  letterSpacing: "0.02em",
  backgroundColor: pillDisabled
    ? "rgba(46,160,80,0.18)"
    : selected
      ? "#FFCD1F"
      : "#343759",
  color: pillDisabled ? "#D4F5E0" : selected ? "#000" : "#ffffff",
  border: pillDisabled
    ? "1px solid rgba(125,222,160,0.55)"
    : "1px solid transparent",
  opacity: 1,
  cursor: pillDisabled ? "default" : "pointer",
  boxShadow: "none",
  flex: "0 0 auto",
  whiteSpace: "nowrap",
  gap: "8px",
  "&.Mui-disabled": {
    opacity: 1,
    color: "#D4F5E0",
  },
  "&:hover": {
    backgroundColor: pillDisabled
      ? "rgba(46,160,80,0.28)"
      : selected
        ? "#e6b800"
        : "#3d4168",
    boxShadow: "none",
  },
}));

const PillGrid = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  alignContent: "flex-start",
  width: "100%",
  minHeight: 72,
  marginLeft: -6,
  marginRight: -6,
});

const VIDEO_STATUS = {
  approved: {
    label: "Approved",
    bg: "rgba(46,160,80,0.22)",
    color: "#7ddea0",
    border: "rgba(46,160,80,0.45)",
  },
  pending: {
    label: "Pending",
    bg: "rgba(230,140,30,0.2)",
    color: "#ffb74d",
    border: "rgba(230,140,30,0.45)",
  },
  rejected: {
    label: "Rejected",
    bg: "rgba(239,54,78,0.18)",
    color: "#ff8a9a",
    border: "rgba(239,54,78,0.45)",
  },
};

function probeClientDuration(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const d = video.duration;
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(d) ? d : null);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      video.src = url;
    } catch (e) {
      resolve(null);
    }
  });
}

export default function CompetitionEnter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("mynetgoals.com-token");
  const loggedUser = useSelector((state) => state.loggedUser.user);
  const profileUser = useSelector((state) => state.user.data);
  const accountUser = profileUser || loggedUser;
  const [guestModal, setGuestModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("idle");
  const [competition, setCompetition] = useState(null);
  const [contestant, setContestant] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");
  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [bio, setBio] = useState("");
  const [hometown, setHometown] = useState("");
  const [profession, setProfession] = useState("");
  const [platformAdvocacy, setPlatformAdvocacy] = useState("");
  const [hobbies, setHobbies] = useState("");
  const [eventId, setEventId] = useState("");
  const [category, setCategory] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [mediaKind, setMediaKind] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const lockedCategory = contestant?.category || "";
  const isRegistered = Boolean(contestant?.id && contestant?.full_name);
  const fieldsLocked = isRegistered;
  const emailLocked = true;

  useEffect(() => {
    if (lockedCategory) setCategory(lockedCategory);
  }, [lockedCategory]);

  const MAX_PROFILE_BYTES = 5 * 1024 * 1024;

  const validateField = (name, value, extras = {}) => {
    const v = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "fullName": {
        if (!v) return "Full name is required";
        if (v.length < 2) return "Full name must be at least 2 characters";
        if (v.length > 100) return "Full name must be 100 characters or less";
        if (!/^[a-zA-Z\s.'-]+$/.test(v))
          return "Full name can only contain letters and spaces";
        return "";
      }
      case "age": {
        if (!v) return "Age is required";
        const n = Number(v);
        if (!Number.isInteger(n) || n < 5 || n > 100)
          return "Enter an age between 5 and 100";
        return "";
      }
      case "email": {
        if (!v) return "Email address is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
          return "Please enter a valid email address";
        return "";
      }
      case "mobile": {
        if (!v && v !== 0) return "Mobile number is required";
        const digits = String(v).replace(/\D/g, "");
        if (!/^\d+$/.test(digits)) return "Mobile number must be digits only";
        if (digits.length !== 10)
          return "Please enter a valid 10-digit mobile number";
        return "";
      }
      case "profile": {
        const hasImage =
          extras.profileFile ||
          extras.contestantImage ||
          extras.profilePreview;
        if (!hasImage) return "Profile picture is required";
        if (extras.profileFile) {
          if (!extras.profileFile.type?.startsWith("image/"))
            return "Please select a valid image file";
          if (extras.profileFile.size > MAX_PROFILE_BYTES)
            return "Profile picture must be 5 MB or less";
        }
        return "";
      }
      case "bio": {
        if (!v) return "Bio is required";
        if (v.length > 1000) return "Bio must be 1000 characters or less";
        return "";
      }
      case "hometown": {
        if (!v) return "Hometown is required";
        if (v.length > 120) return "Hometown must be 120 characters or less";
        return "";
      }
      case "profession": {
        if (!v) return "Profession is required";
        if (v.length > 300) return "Profession must be 300 characters or less";
        return "";
      }
      case "platformAdvocacy": {
        if (!v) return `${ADVOCACY_PLATFORM_LABEL} is required`;
        if (v.length > 500)
          return `${ADVOCACY_PLATFORM_LABEL} must be 500 characters or less`;
        return "";
      }
      case "hobbies": {
        if (!v) return "Hobbies are required";
        if (v.length > 500) return "Hobbies must be 500 characters or less";
        return "";
      }
      default:
        return "";
    }
  };

  const validateRegistration = ({ showToast = true } = {}) => {
    const next = {
      fullName: validateField("fullName", fullName),
      age: validateField("age", age),
      email: validateField("email", email),
      mobile: validateField("mobile", mobile),
      bio: validateField("bio", bio),
      hometown: validateField("hometown", hometown),
      profession: validateField("profession", profession),
      platformAdvocacy: validateField("platformAdvocacy", platformAdvocacy),
      hobbies: validateField("hobbies", hobbies),
      profile: validateField("profile", null, {
        profileFile,
        contestantImage: contestant?.profile_image,
        profilePreview,
      }),
    };
    setFieldErrors(next);
    setTouched({
      fullName: true,
      age: true,
      email: true,
      mobile: true,
      bio: true,
      hometown: true,
      profession: true,
      platformAdvocacy: true,
      hobbies: true,
      profile: true,
    });
    const first = Object.values(next).find(Boolean);
    if (first && showToast) toast.error(first);
    return !first;
  };

  const markTouched = (name) =>
    setTouched((t) => ({ ...t, [name]: true }));

  const onFieldChange = (name, setter) => (e) => {
    const value = e.target.value;
    setter(value);
    if (touched[name] || fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const load = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [detailRes, mineRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL_USER}/getCompetitionDetail/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL_USER}/getMyContestant/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      const detail = detailRes.data.success ? detailRes.data.data : null;
      if (detail) setCompetition(detail);
      const raw = mineRes.data?.data;
      const mine = Array.isArray(raw) ? raw[0] || null : raw || null;
      setContestant(mine);
      setProfilePreview(mine?.profile_image || loggedUser?.profile_img || "");
      setProfileFile(null);
      const identityName =
        mine?.full_name || accountFullName(accountUser) || "";
      const identityEmail =
        accountEmail(accountUser) || mine?.email || "";
      setFullName(identityName);
      setEmail(identityEmail);
      if (mine?.full_name) {
        setAge(contestantAgeValue(mine) || "");
        const parsed = splitStoredMobile(mine.mobile);
        setCountryCode(parsed.countryCode);
        setMobile(parsed.mobile);
        setBio(mine.bio || "");
        setHometown(mine.hometown || "");
        setProfession(mine.profession || "");
        setPlatformAdvocacy(mine.platform_advocacy || "");
        setHobbies(mine.hobbies || "");
      } else {
        setAge("");
        const accountPhone =
          accountUser?.phone_number || accountUser?.mobile || "";
        if (accountPhone) {
          const parsed = splitStoredMobile(accountPhone);
          setCountryCode(parsed.countryCode);
          setMobile(parsed.mobile);
        } else {
          setMobile("");
          setCountryCode(DEFAULT_COUNTRY_CODE);
        }
        setBio("");
        setHometown("");
        setProfession("");
        setPlatformAdvocacy("");
        setHobbies("");
      }
      return { competition: detail, contestant: mine };
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    const mail = accountEmail(accountUser);
    if (mail) setEmail(mail);
  }, [accountUser]);

  const requireRegistered = () => {
    if (loggedUser?.device_id) {
      setGuestModal(true);
      return false;
    }
    return true;
  };

  /** Lock events that already have pending/approved video; allow re-upload when rejected */
  const lockedEventIds = useMemo(() => {
    const ids = new Set();
    (contestant?.videos || []).forEach((v) => {
      const st = String(v?.status || "").toLowerCase();
      if (v?.event_id != null && st !== "rejected") {
        ids.add(String(v.event_id));
      }
    });
    return ids;
  }, [contestant]);

  const allEvents = competition?.events || [];
  const categories = competition?.categories || [];
  const myVideos = Array.isArray(contestant?.videos) ? contestant.videos : [];
  const canUpload = competition?.status === "running";
  const registrationOpen = isCompetitionRegistrationOpen(competition);
  const canParticipate = canUpload && registrationOpen;
  const hasAvailableEvent = allEvents.some(
    (e) => !lockedEventIds.has(String(e.id))
  );
  const allEventsUploaded =
    allEvents.length > 0 &&
    allEvents.every((e) => lockedEventIds.has(String(e.id)));

  const onProfileChange = (e) => {
    if (!requireRegistered()) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    markTouched("profile");
    if (!file.type?.startsWith("image/")) {
      setFieldErrors((prev) => ({
        ...prev,
        profile: "Please select a valid image file",
      }));
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > MAX_PROFILE_BYTES) {
      setFieldErrors((prev) => ({
        ...prev,
        profile: "Profile picture must be 5 MB or less",
      }));
      toast.error("Profile picture must be 5 MB or less");
      return;
    }
    setProfileFile(file);
    setProfilePreview(URL.createObjectURL(file));
    setFieldErrors((prev) => ({ ...prev, profile: "" }));
  };

  const saveRegistration = async ({ silentToast } = {}) => {
    if (!canParticipate) {
      const msg = !registrationOpen
        ? "Registration and uploads are closed for this competition"
        : "Registration and uploads are only available while the competition is Active";
      if (!silentToast) toast.error(msg);
      return null;
    }
    const nameUnchanged =
      isRegistered &&
      fullName.trim() === String(contestant?.full_name || "").trim();
    if (isRegistered && !profileFile && nameUnchanged) {
      return contestant;
    }
    if (isRegistered && !nameUnchanged) {
      const nameErr = validateField("fullName", fullName);
      if (nameErr) {
        setFieldErrors((prev) => ({ ...prev, fullName: nameErr }));
        if (!silentToast) toast.error(nameErr);
        return null;
      }
    }
    if (!isRegistered && !validateRegistration({ showToast: !silentToast })) {
      return null;
    }
    if (isRegistered && profileFile) {
      const profileErr = validateField("profile", null, {
        profileFile,
        contestantImage: contestant?.profile_image,
        profilePreview,
      });
      if (profileErr) {
        setFieldErrors((prev) => ({ ...prev, profile: profileErr }));
        toast.error(profileErr);
        return null;
      }
    } else if (!isRegistered && !profileFile) {
      toast.error("Please upload a profile picture");
      return null;
    }

    const data = new FormData();
    data.append("competition_id", id);
    if (!isRegistered) {
      data.append("full_name", fullName.trim());
      data.append("age", age);
      const approxDob = dobFromAge(age);
      if (approxDob) data.append("dob", approxDob);
      data.append("email", email.trim());
      data.append(
        "mobile",
        `${countryCode.split(" ")[0]}${String(mobile).replace(/\D/g, "")}`
      );
      data.append("bio", bio.trim());
      data.append("hometown", hometown.trim());
      data.append("profession", profession.trim());
      data.append("platform_advocacy", platformAdvocacy.trim());
      data.append("hobbies", hobbies.trim());
    } else if (fullName.trim()) {
      data.append("full_name", fullName.trim());
    }
    if (profileFile) data.append("profile_image", profileFile);

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL_USER}/upsertContestant`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.data.success) {
      toast.error(res.data.message || "Registration save failed");
      return null;
    }
    if (!silentToast) {
      toast.success(res.data.message || "Registration saved");
    }
    const next = res.data.data;
    setContestant((prev) => ({
      ...(prev || {}),
      ...next,
      videos: prev?.videos || next?.videos || [],
    }));
    setProfilePreview(next?.profile_image || profilePreview);
    setProfileFile(null);
    setFieldErrors({});
    return next;
  };

  const acceptMediaFile = (file) => {
    if (!file) return;
    if (isCompetitionImageFile(file)) {
      setMediaKind("image");
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setUploadProgress(0);
      return;
    }
    if (isCompetitionVideoFile(file)) {
      setMediaKind("video");
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setUploadProgress(0);
      return;
    }
    toast.error("File type not supported. Upload a video or image (JPG, PNG, WEBP, GIF).");
  };

  const onVideoInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    acceptMediaFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptMediaFile(e.dataTransfer?.files?.[0]);
  };

  const clearVideo = () => {
    setVideoFile(null);
    setVideoPreview("");
    setMediaKind(null);
    setUploadProgress(0);
    setUploadPhase("idle");
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const publish = async () => {
    if (!requireRegistered()) return;
    if (!canParticipate) {
      toast.error(
        !registrationOpen
          ? "Registration and uploads are closed for this competition"
          : "Uploads are only available while the competition is Active"
      );
      return;
    }
    if (!eventId) {
      toast.error("Select an event");
      return;
    }
    if (!category) {
      toast.error("Select a category");
      return;
    }
    if (lockedEventIds.has(String(eventId))) {
      toast.error("You already have an upload for this event");
      return;
    }
    if (!videoFile) {
      toast.error("Choose a video or image to upload");
      return;
    }
    if (mediaKind === "video") {
      const duration = await probeClientDuration(videoFile);
      if (duration != null && duration > MAX_VIDEO_SEC) {
        toast.error("Video must be 10 minutes or less");
        return;
      }
    }

    setSaving(true);
    setUploadProgress(0);
    setUploadPhase("uploading");
    try {
      const saved = await saveRegistration({ silentToast: true });
      if (!saved?.id) return;

      const data = new FormData();
      data.append("competition_id", id);
      data.append("event_id", eventId);
      data.append("category", category);
      data.append("contestant_id", saved.id);
      if (mediaKind === "image") {
        data.append("image", videoFile);
      } else {
        data.append("video", videoFile);
      }
      const fileSize = videoFile.size || 0;
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL_USER}/uploadCompetitionEventVideo`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (evt) => {
            const total = evt.total || fileSize;
            if (!total) return;
            const pct = Math.min(90, Math.round((evt.loaded * 90) / total));
            setUploadProgress(pct);
            if (evt.loaded >= total) {
              setUploadPhase("processing");
              setUploadProgress(92);
            }
          },
        }
      );
      setUploadPhase("processing");
      setUploadProgress(96);
      if (res.data.success) {
        setUploadProgress(100);
        const baseMsg =
          res.data.message ||
          "Submitted successfully. Profile and upload are pending admin approval.";
        const refreshed = await load({ silent: true });
        const events = refreshed?.competition?.events || [];
        const videos = refreshed?.contestant?.videos || [];
        const locked = new Set(
          videos
            .filter((v) => String(v.status).toLowerCase() !== "rejected")
            .map((v) => String(v.event_id))
            .filter(Boolean)
        );
        const allDone =
          events.length > 0 &&
          events.every((ev) => locked.has(String(ev.id)));
        const msg = allDone
          ? `${baseMsg} You have submitted uploads for all events.`
          : baseMsg;
        toast.success(msg, { autoClose: 5000 });
        setSuccessMessage(msg);
        clearVideo();
        setEventId("");
        setCategory("");
        setUploadPhase("idle");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(res.data.message || "Upload failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Upload failed");
    } finally {
      setSaving(false);
      setUploadPhase("idle");
    }
  };

  return (
    <>
      <ToastContainer />
      <GuestUserRistModal open={guestModal} setOpen={setGuestModal} />
      <Header />
      <Box className="userLayout addGoal">
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            background: "#242743",
            minHeight: "70vh",
            pb: 4,
            width: "100%",
            overflowX: "hidden",
          }}
        >
          <Box className="addGoal-wrapper addTournament-wrapper">
            <Box className="addGoal-inner">
              <Box className="addGoal-header">
                <Button
                  sx={{ alignItems: "center", gap: "5px" }}
                  onClick={() => navigate(`/competitions/${id}`)}
                >
                  <KeyboardBackspaceIcon
                    sx={{
                      color: "white",
                      fontSize: { xs: "20px", md: "30px" },
                    }}
                  />
                  <Typography component="h3">Back</Typography>
                </Button>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    pr: { xs: 6, md: 8 },
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: "15px !important", sm: "inherit" },
                      lineHeight: { xs: "1.2 !important", sm: "inherit" },
                      px: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    REGISTER FOR COMPETITION
                  </Typography>
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress sx={{ color: "#ef364e" }} />
                </Box>
              ) : !canParticipate ? (
                <Alert severity="warning" sx={{ m: 2, width: "100%" }}>
                  {!registrationOpen
                    ? "Registration and uploads are closed for this competition."
                    : "Registration and uploads are only available while the competition is Active."}
                </Alert>
              ) : (
                <Box className="addGoal-form" sx={{ gap: 2.5 }}>
                  {successMessage ? (
                    <Alert
                      severity="success"
                      onClose={() => setSuccessMessage("")}
                      sx={{ width: "100%" }}
                    >
                      {successMessage}
                    </Alert>
                  ) : null}

                  {contestant?.profile_status === "rejected" &&
                  contestant?.profile_rejection_reason ? (
                    <Alert severity="error" sx={{ width: "100%" }}>
                      Profile note: {contestant.profile_rejection_reason}
                    </Alert>
                  ) : null}

                  {/* —— Section A: Contestant registration —— */}
                  <Box
                    sx={{
                      width: "100%",
                      p: { xs: 2, md: 3 },
                      borderRadius: "14px",
                      background:
                        "linear-gradient(160deg, rgba(255,205,31,0.08) 0%, rgba(36,39,67,0.95) 40%)",
                      border: "1px solid rgba(255,205,31,0.28)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#FFCD1F",
                        fontWeight: 800,
                        fontSize: 13,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Contestant registration
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 13,
                        mb: 2.5,
                      }}
                    >
                      {fieldsLocked
                        ? "Your details are locked except your competition display name and profile picture."
                        : "Your name is prefilled from your account but you can change how it appears in this competition. Email cannot be changed."}
                    </Typography>

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={3}
                      alignItems={{ xs: "center", md: "flex-start" }}
                    >
                      <Box className="profile-wrapper-outter" sx={{ m: 0 }}>
                        <Box className="profile-wrapper">
                          <Box className="formProfile">
                            <input
                              type="file"
                              accept="image/*"
                              ref={imageInputRef}
                              onChange={onProfileChange}
                              style={{ display: "none" }}
                            />
                            <Box
                              className="option-outter"
                              sx={{
                                padding: "0",
                                cursor: "pointer",
                                outline:
                                  touched.profile && fieldErrors.profile
                                    ? "2px solid #ff8a9a"
                                    : "none",
                                borderRadius: "50%",
                              }}
                              onClick={() => imageInputRef.current?.click()}
                            >
                              <div className="option-inner">
                                <img
                                  src={profilePreview || profilePlaceholder}
                                  alt="Profile picture placeholder"
                                  className="img_fluid"
                                  style={
                                    profilePreview
                                      ? { objectFit: "cover" }
                                      : {
                                          width: "78%",
                                          height: "auto",
                                          objectFit: "contain",
                                        }
                                  }
                                />
                              </div>
                            </Box>
                            <Button
                              className="icon-wrapper"
                              onClick={() => imageInputRef.current?.click()}
                            >
                              <div className="img-div">
                                <img
                                  src={cameraIcon}
                                  alt=""
                                  className="img_fluid"
                                />
                              </div>
                            </Button>
                          </Box>
                        </Box>
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            fontSize: 12,
                            textAlign: "center",
                            mt: 1,
                          }}
                        >
                          Profile picture
                        </Typography>
                      </Box>

                      <Stack spacing={2} sx={{ flex: 1, width: "100%" }}>
                        <TextField
                          label="Full Name"
                          value={fullName}
                          onChange={onFieldChange("fullName", setFullName)}
                          onBlur={async () => {
                            markTouched("fullName");
                            setFieldErrors((prev) => ({
                              ...prev,
                              fullName: validateField("fullName", fullName),
                            }));
                            if (
                              isRegistered &&
                              fullName.trim() !==
                                String(contestant?.full_name || "").trim()
                            ) {
                              await saveRegistration({ silentToast: false });
                            }
                          }}
                          error={Boolean(touched.fullName && fieldErrors.fullName)}
                          helperText={
                            touched.fullName && fieldErrors.fullName
                              ? fieldErrors.fullName
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label="Age"
                          type="number"
                          value={age}
                          onChange={onFieldChange("age", setAge)}
                          onBlur={() => {
                            markTouched("age");
                            setFieldErrors((prev) => ({
                              ...prev,
                              age: validateField("age", age),
                            }));
                          }}
                          disabled={fieldsLocked}
                          inputProps={{ min: 5, max: 100, step: 1 }}
                          error={Boolean(touched.age && fieldErrors.age)}
                          helperText={
                            touched.age && fieldErrors.age
                              ? fieldErrors.age
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label="Email Address"
                          type="email"
                          value={email}
                          onChange={onFieldChange("email", setEmail)}
                          onBlur={() => {
                            markTouched("email");
                            setFieldErrors((prev) => ({
                              ...prev,
                              email: validateField("email", email),
                            }));
                          }}
                          disabled={emailLocked}
                          error={Boolean(touched.email && fieldErrors.email)}
                          helperText={
                            touched.email && fieldErrors.email
                              ? fieldErrors.email
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <Box>
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <TextField
                              select
                              label="Code"
                              value={countryCode}
                              onChange={(e) => setCountryCode(e.target.value)}
                              disabled={fieldsLocked}
                              SelectProps={{
                                renderValue: (selected) =>
                                  String(selected).split(" ").shift(),
                              }}
                              sx={{
                                ...fieldSx,
                                width: 118,
                                flexShrink: 0,
                                "& .MuiSelect-icon": {
                                  color: "rgba(255,255,255,0.55)",
                                },
                              }}
                            >
                              {countryCodes.map((value) => (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="Mobile Number"
                              value={mobile}
                              type="tel"
                              inputMode="numeric"
                              onChange={(e) => {
                                const digits = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                setMobile(digits);
                                if (touched.mobile || fieldErrors.mobile) {
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    mobile: validateField("mobile", digits),
                                  }));
                                }
                              }}
                              onBlur={() => {
                                markTouched("mobile");
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  mobile: validateField("mobile", mobile),
                                }));
                              }}
                              disabled={fieldsLocked}
                              placeholder="Mobile Number"
                              error={Boolean(
                                touched.mobile && fieldErrors.mobile
                              )}
                              sx={{
                                ...fieldSx,
                                flexGrow: 1,
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                  {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              color:
                                touched.mobile && fieldErrors.mobile
                                  ? "#ff8a9a"
                                  : "transparent",
                              fontSize: 12,
                              minHeight: 20,
                              mt: 0.5,
                              ml: 0.5,
                            }}
                          >
                            {(touched.mobile && fieldErrors.mobile) || " "}
                          </Typography>
                        </Box>
                        <TextField
                          label="Bio"
                          value={bio}
                          onChange={onFieldChange("bio", setBio)}
                          onBlur={() => {
                            markTouched("bio");
                            setFieldErrors((prev) => ({
                              ...prev,
                              bio: validateField("bio", bio),
                            }));
                          }}
                          disabled={fieldsLocked}
                          multiline
                          minRows={3}
                          placeholder="Tell us about yourself…"
                          error={Boolean(touched.bio && fieldErrors.bio)}
                          helperText={
                            touched.bio && fieldErrors.bio ? fieldErrors.bio : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label="Hometown"
                          value={hometown}
                          onChange={onFieldChange("hometown", setHometown)}
                          onBlur={() => {
                            markTouched("hometown");
                            setFieldErrors((prev) => ({
                              ...prev,
                              hometown: validateField("hometown", hometown),
                            }));
                          }}
                          disabled={fieldsLocked}
                          placeholder="e.g. London, UK"
                          error={Boolean(touched.hometown && fieldErrors.hometown)}
                          helperText={
                            touched.hometown && fieldErrors.hometown
                              ? fieldErrors.hometown
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label="Profession"
                          value={profession}
                          onChange={onFieldChange("profession", setProfession)}
                          onBlur={() => {
                            markTouched("profession");
                            setFieldErrors((prev) => ({
                              ...prev,
                              profession: validateField("profession", profession),
                            }));
                          }}
                          disabled={fieldsLocked}
                          placeholder="e.g. Recent graduate and community program coordinator"
                          error={Boolean(touched.profession && fieldErrors.profession)}
                          helperText={
                            touched.profession && fieldErrors.profession
                              ? fieldErrors.profession
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label={ADVOCACY_PLATFORM_LABEL}
                          value={platformAdvocacy}
                          onChange={onFieldChange(
                            "platformAdvocacy",
                            setPlatformAdvocacy
                          )}
                          onBlur={() => {
                            markTouched("platformAdvocacy");
                            setFieldErrors((prev) => ({
                              ...prev,
                              platformAdvocacy: validateField(
                                "platformAdvocacy",
                                platformAdvocacy
                              ),
                            }));
                          }}
                          disabled={fieldsLocked}
                          multiline
                          minRows={2}
                          placeholder='e.g. "Code for All" – bridging the digital divide for youth'
                          error={Boolean(
                            touched.platformAdvocacy && fieldErrors.platformAdvocacy
                          )}
                          helperText={
                            touched.platformAdvocacy && fieldErrors.platformAdvocacy
                              ? fieldErrors.platformAdvocacy
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        <TextField
                          label="Hobbies"
                          value={hobbies}
                          onChange={onFieldChange("hobbies", setHobbies)}
                          onBlur={() => {
                            markTouched("hobbies");
                            setFieldErrors((prev) => ({
                              ...prev,
                              hobbies: validateField("hobbies", hobbies),
                            }));
                          }}
                          disabled={fieldsLocked}
                          multiline
                          minRows={2}
                          placeholder="e.g. Playing the classical piano, long-distance running, and oil painting"
                          error={Boolean(touched.hobbies && fieldErrors.hobbies)}
                          helperText={
                            touched.hobbies && fieldErrors.hobbies
                              ? fieldErrors.hobbies
                              : " "
                          }
                          FormHelperTextProps={{
                            sx: {
                              color: "#ff8a9a !important",
                              minHeight: 20,
                              m: 0,
                              mt: 0.5,
                            },
                          }}
                          sx={fieldSx}
                        />
                        {touched.profile && fieldErrors.profile ? (
                          <Typography
                            sx={{ color: "#ff8a9a", fontSize: 13, mt: -1 }}
                          >
                            {fieldErrors.profile}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Box>

                  {/* —— Section B: Media upload —— */}
                  <Box
                    sx={{
                      width: "100%",
                      p: { xs: 2, md: 3 },
                      borderRadius: "14px",
                      background:
                        "linear-gradient(160deg, rgba(239,54,78,0.12) 0%, rgba(20,22,40,0.98) 45%)",
                      border: "1px solid rgba(239,54,78,0.35)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#ff8a9a",
                        fontWeight: 800,
                        fontSize: 13,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Competition media
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
                      Select an event and category, then upload a video (max 10
                      minutes) or an image. Rejected uploads can be
                      re-submitted.
                    </Typography>

                    {allEventsUploaded ? (
                      <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
                        You have submitted an upload for every event. Check My
                        uploads below for approval status.
                      </Alert>
                    ) : (
                      <>
                        <Box className="category form-control" sx={{ mb: 1 }}>
                          <Typography className="label">Event</Typography>
                          <PillGrid>
                            {allEvents.length === 0 ? (
                              <Typography
                                sx={{ color: "rgba(255,255,255,0.5)", m: 1 }}
                              >
                                No events available
                              </Typography>
                            ) : (
                              allEvents.map((ev) => {
                                const used = lockedEventIds.has(String(ev.id));
                                const selected =
                                  String(eventId) === String(ev.id);
                                const uploaded = myVideos.find(
                                  (v) => String(v.event_id) === String(ev.id)
                                );
                                const st = String(
                                  uploaded?.status || ""
                                ).toLowerCase();
                                const statusLabel =
                                  VIDEO_STATUS[st]?.label ||
                                  (used ? "Uploaded" : "");
                                const tip = used
                                  ? `Already submitted for ${ev.name} (${statusLabel}).`
                                  : st === "rejected"
                                    ? `Re-upload for ${ev.name} (previous was rejected)`
                                    : `Select ${ev.name}`;
                                return (
                                  <Tooltip
                                    key={ev.id}
                                    title={tip}
                                    arrow
                                    placement="top"
                                    enterTouchDelay={0}
                                  >
                                    <span>
                                      <Pill
                                        selected={selected}
                                        pillDisabled={used}
                                        onClick={() => {
                                          if (used) return;
                                          setEventId(ev.id);
                                        }}
                                      >
                                        {used ? (
                                          <CheckCircleIcon
                                            sx={{ fontSize: 20 }}
                                          />
                                        ) : null}
                                        {ev.name}
                                        {used ? (
                                          <Box
                                            component="span"
                                            sx={{
                                              ml: 0.5,
                                              px: 1,
                                              py: 0.25,
                                              borderRadius: 10,
                                              fontSize: 12,
                                              fontWeight: 700,
                                              bgcolor: "rgba(46,160,80,0.35)",
                                              color: "#fff",
                                            }}
                                          >
                                            {statusLabel || "Uploaded"}
                                          </Box>
                                        ) : st === "rejected" ? (
                                          <Box
                                            component="span"
                                            sx={{
                                              ml: 0.5,
                                              px: 1,
                                              py: 0.25,
                                              borderRadius: 10,
                                              fontSize: 12,
                                              fontWeight: 700,
                                              bgcolor: "rgba(239,54,78,0.45)",
                                              color: "#fff",
                                            }}
                                          >
                                            Re-upload
                                          </Box>
                                        ) : null}
                                      </Pill>
                                    </span>
                                  </Tooltip>
                                );
                              })
                            )}
                          </PillGrid>
                        </Box>

                        <Box className="category form-control" sx={{ mb: 2 }}>
                          <Typography className="label">Category</Typography>
                          {lockedCategory ? (
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                              Your category for this competition is locked to{" "}
                              <strong>{lockedCategory}</strong> (from your first
                              upload).
                            </Alert>
                          ) : null}
                          <PillGrid>
                            {categories.map((c) => {
                              const selected = category === c.category;
                              const pillDisabled =
                                Boolean(
                                  lockedCategory && lockedCategory !== c.category
                                );
                              return (
                                <Pill
                                  key={c.category}
                                  selected={selected}
                                  pillDisabled={pillDisabled}
                                  onClick={() =>
                                    !pillDisabled && setCategory(c.category)
                                  }
                                >
                                  {c.category}
                                </Pill>
                              );
                            })}
                          </PillGrid>
                        </Box>

                        <Box
                          className="upload-video-wrapper"
                          sx={{ width: "100%", mb: 2 }}
                        >
                          <Box
                            className={
                              dragging
                                ? "drag-area upload-video"
                                : "upload-video"
                            }
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragging(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setDragging(false);
                            }}
                            onDrop={onDrop}
                            sx={{
                              minHeight: 220,
                              width: "100%",
                              borderRadius: "8px",
                              border: dragging
                                ? "2px dashed #ef364e"
                                : "1px solid rgba(239,54,78,0.35)",
                              bgcolor: "rgba(0,0,0,0.28)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {videoPreview ? (
                              <Box sx={{ width: "100%", position: "relative" }}>
                                <Button
                                  onClick={clearVideo}
                                  disabled={saving}
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                    minWidth: 36,
                                    color: "#fff",
                                    bgcolor: "rgba(0,0,0,0.45)",
                                  }}
                                >
                                  <ClearIcon />
                                </Button>
                                {mediaKind === "image" ? (
                                  <Box
                                    component="img"
                                    src={videoPreview}
                                    alt="Preview"
                                    sx={{
                                      width: "100%",
                                      maxHeight: 300,
                                      display: "block",
                                      objectFit: "contain",
                                      bgcolor: "#000",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    component="video"
                                    src={videoPreview}
                                    controls
                                    muted
                                    sx={{
                                      width: "100%",
                                      maxHeight: 300,
                                      display: "block",
                                      bgcolor: "#000",
                                    }}
                                  />
                                )}
                              </Box>
                            ) : (
                              <Button
                                onClick={() => videoInputRef.current?.click()}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "#fff",
                                  textTransform: "none",
                                  width: "100%",
                                  py: 6,
                                }}
                              >
                                <FileUploadIcon
                                  sx={{ fontSize: 48, color: "#ef364e" }}
                                />
                                <Typography
                                  sx={{ fontWeight: 700, fontSize: 17 }}
                                >
                                  Drop video or image here or tap to browse
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.45)",
                                    fontSize: 13,
                                  }}
                                >
                                  Video: MP4, MOV, WEBM · Image: JPG, PNG, WEBP
                                  · video up to 10 minutes
                                </Typography>
                              </Button>
                            )}
                            <input
                              ref={videoInputRef}
                              type="file"
                              accept={competitionMediaAccept}
                              style={{ display: "none" }}
                              onChange={onVideoInput}
                            />
                          </Box>
                        </Box>

                        {saving || uploadProgress > 0 ? (
                          <Box sx={{ width: "100%", mb: 2 }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              mb={0.6}
                            >
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.7)",
                                  fontSize: 13,
                                }}
                              >
                                {uploadPhase === "processing"
                                  ? "Processing on server…"
                                  : uploadProgress >= 100
                                    ? "Done"
                                    : "Uploading…"}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.7)",
                                  fontSize: 13,
                                }}
                              >
                                {uploadProgress}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant={
                                uploadPhase === "processing" &&
                                uploadProgress < 100
                                  ? "indeterminate"
                                  : "determinate"
                              }
                              value={uploadProgress}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: "rgba(255,255,255,0.08)",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor: "#ef364e",
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                        ) : null}

                        <Button
                          fullWidth
                          variant="contained"
                          disabled={
                            saving || !videoFile || !hasAvailableEvent
                          }
                          onClick={publish}
                          sx={{
                            bgcolor: "#ef364e",
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 16,
                            py: 1.4,
                            borderRadius: "8px",
                            "&:hover": { bgcolor: "#d32d43" },
                            "&.Mui-disabled": {
                              bgcolor: "rgba(239,54,78,0.4)",
                              color: "rgba(255,255,255,0.9)",
                            },
                          }}
                        >
                          {saving
                            ? "Submitting…"
                            : isRegistered
                              ? "Submit upload"
                              : "Submit registration & upload"}
                        </Button>
                      </>
                    )}
                  </Box>

                  {/* My uploads */}
                  <Box
                    id="my-uploads"
                    sx={{
                      width: "100%",
                      p: { xs: 2, md: 2.5 },
                      borderRadius: "8px",
                      bgcolor: "rgba(0,0,0,0.22)",
                      border: "1px solid #414466",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 18,
                        mb: 0.5,
                      }}
                    >
                      My uploads
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
                      Approval status and rejection comments
                    </Typography>

                    {myVideos.length === 0 ? (
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.45)",
                          fontSize: 14,
                        }}
                      >
                        No uploads yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1.25}>
                        {myVideos.map((v) => {
                          const status = String(
                            v.status || "pending"
                          ).toLowerCase();
                          const mediaType = resolveCompetitionMediaType(v);
                          const thumbSrc =
                            v.thumbnail ||
                            (mediaType === "image" ? v.video_url : null);
                          const meta = VIDEO_STATUS[status] || {
                            label: status,
                            bg: "rgba(255,255,255,0.08)",
                            color: "#fff",
                            border: "rgba(255,255,255,0.2)",
                          };
                          return (
                            <Box
                              key={v.id}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                bgcolor: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1.5}
                              >
                                <Box
                                  sx={{
                                    width: 72,
                                    height: 48,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    bgcolor: "#111",
                                    backgroundImage: thumbSrc
                                      ? `url(${thumbSrc})`
                                      : "none",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    noWrap
                                    sx={{
                                      color: "#fff",
                                      fontWeight: 700,
                                      fontSize: 14,
                                    }}
                                  >
                                    {v.event?.name || `Event #${v.event_id}`}
                                  </Typography>
                                  <Typography
                                    noWrap
                                    sx={{
                                      color: "rgba(255,255,255,0.5)",
                                      fontSize: 12,
                                      mt: 0.25,
                                    }}
                                  >
                                    {v.category || "—"} ·{" "}
                                    {mediaType === "image" ? "Image" : "Video"}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  label={meta.label}
                                  sx={{
                                    fontWeight: 700,
                                    bgcolor: meta.bg,
                                    color: meta.color,
                                    border: `1px solid ${meta.border}`,
                                    flexShrink: 0,
                                  }}
                                />
                              </Stack>
                              {status === "rejected" && v.rejection_reason ? (
                                <Alert
                                  severity="error"
                                  sx={{
                                    mt: 1.25,
                                    py: 0,
                                    bgcolor: "rgba(239,54,78,0.12)",
                                    color: "#ffb4be",
                                    "& .MuiAlert-icon": { color: "#ff8a9a" },
                                  }}
                                >
                                  {v.rejection_reason}
                                </Alert>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Footer />
        </Container>
      </Box>
    </>
  );
}
